/**
 * @file Saga renewing one contract into a follow-up contract: a 200 snackbars and a 207 opens the renewal's partial dialog.
 * Failure path: catch → ErrorHandlerSequence with the route's expected errors → GLOBAL_CONTRACT_EXTEND_FAILURE.
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence, ExpectedErrors } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import Time from "../../Time/Time";
import ContractFactory from "../../Entity/Factories/ContractFactory";
// import ChargeTemplateFactory from "../../Entity/Factories/ChargeTemplateFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ChargeTemplateInterface } from "../../Entity/Entities/Charge/ChargeTemplateInterface";
import { MainTenant } from "../../Entity/Entities/ContactForContract/Type/Types/MainTenant";
import { isContractIdOnly } from "../../Entity/Entities/Contract/PartialAnswer";
import Translator from "../../Translation/Translator";

const EXPECTED_ERRORS: ExpectedErrors = {
  be_ajv_not_validated: null,
  financial_operations_were_blocked: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-financial-processing-busy" },
  be_contract_not_found: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-not-found" },
  be_last_earlier_than_first_date: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-last-date" },
  cant_create_contract_without_rent_template: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-no-rent-template" },
  first_date_is_required: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-template-first-date" },
  should_be_filled_one_of_the_fees_types: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-fees" },
  be_main_tenant_missing: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-tenant-no-contact" },
  be_server_error: { titleKey: "refusal-title-contract-renew", bodyKey: "refusal-body-contract-renew-server-error" },
  cannot_renew_with_disabled_statuses: { titleKey: "refusal-title-contract-renew", bodyKey: "cannot_renew_with_disabled_statuses" },
  cannot_extend_contract_for_not_later_date: { titleKey: "refusal-title-contract-renew", bodyKey: "cannot_extend_contract_for_not_later_date" },
  cannot_renew_the_contract: { titleKey: "refusal-title-contract-renew", bodyKey: "cannot_renew_the_contract" },
  be_cannot_extend_extended_contract: { titleKey: "refusal-title-contract-renew", bodyKey: "be_cannot_extend_extended_contract" },
  be_contract_renewal_source_end_failed: { titleKey: "refusal-title-contract-renew", bodyKey: "be_contract_renewal_source_end_failed" },
  be_contract_renewal_create_failed: { titleKey: "refusal-title-contract-renew", bodyKey: "be_contract_renewal_create_failed" }
};

export default function* contractExtend(action: ActionInterface): any {
  try {
    yield put({
      type: Constants.GLOBAL_CONTRACT_EXTEND_ERRORS_CLEAR
    });

    yield put({
      type: Constants.GLOBAL_CONTRACT_EXTEND_REQUEST
    });

    const contactForContracts: any[] = [];

    // First, we have to push Tenant into the array of Persons.
    if (!!action.payload?.selectedPerson) {
      contactForContracts.push({
      contactId: action.payload?.сreatedFromCoach ? null : action.payload?.selectedPerson?.id || null,
      contractPartyDescription: "Tenant",
      contactForContractTypeId: MainTenant.contactForContractTypeId
    });
  }

    // If we have additional fields, we have to push those into the array too.
    action.payload?.additionalFields.forEach(field => {
      if (!field) {
        return;
      }

      if (typeof field.selectedPerson === "undefined") {
        return;
      }

      contactForContracts.push({
        contactId: field.selectedPerson && field.selectedPerson.id,
        contractPartyDescription: field.description,
        contactForContractTypeId: field.contactForContractTypeId
      });
    });

    let chargeTemplates;

    if(action.payload?.сreatedOnFrontend){
      chargeTemplates = action.payload?.chargeTemplates.map((chargeTemplate: ChargeTemplateInterface) => {
        return {
          amount: chargeTemplate.amount,
          currencyId: chargeTemplate.currency,
          type: chargeTemplate.type,
          repeatPeriodId: chargeTemplate.repeatPeriod,
          description: chargeTemplate.description,
          paymentOffset: chargeTemplate.paymentOffset,
          dueDateFirstPayment: Time.formatOrNull(chargeTemplate.dueDateFirstPayment),
          firstDate: Time.formatOrNull(chargeTemplate.firstDate),
          endDate: Time.formatOrNull(chargeTemplate.endDate),
          paymentRouteId: chargeTemplate.paymentRouteId,
          contactId: chargeTemplate.contactId,
          companyId: chargeTemplate.companyId,
          isCollective: chargeTemplate?.isCollective,
        };
      })
    } else {
      action.payload?.chargeTemplates.map((chargeTemplate: ChargeTemplateInterface) => {
        return {
          id: chargeTemplate.id,
          amount: chargeTemplate.amount,
          currency: chargeTemplate.currency.getCurrencyId(),
          type: chargeTemplate.type.id,
          repeatPeriod: chargeTemplate?.repeatPeriod?.id,
          description: chargeTemplate.description,
          paymentOffset: chargeTemplate.paymentOffset?.getValue(),
          dueDateFirstPayment: Time.formatOrNull(chargeTemplate.dueDateFirstPayment),
          firstDate: Time.formatOrNull(chargeTemplate.firstDate),
          endDate: Time.formatOrNull(chargeTemplate.endDate),
          paymentRouteId: chargeTemplate.paymentRouteId,
          contactId: chargeTemplate.contactId,
          companyId: chargeTemplate.companyId,
          isCollective: chargeTemplate?.isCollective
        };
      })
    }

    // const res = yield call(Http.post, `contract/${action.payload?.id}/extend`, {
    const res = yield call(Http.post, `contract/${action.payload?.id}/extend`, {
      unitId: action.payload?.unit && action.payload?.unit.id,
      id: action.payload?.id,
      amazonFolderName: action.payload?.amazonFolderName,
      note: action.payload?.note,
      referenceForTenant: action.payload?.referenceForTenant,
      contactForContracts: contactForContracts,
      mainTenantContactId: action.payload?.selectedPerson?.id,
      mainTenantCompanyId: action.payload?.selectedCompany?.id,
      extensionDate: Time.formatOrNull(action.payload?.extensionDate),
      lastDate: Time.formatOrNull(action.payload?.lastDate),
      finalRentalPeriodDate: Time.formatOrNull(action.payload?.finalRentalPeriodDate),
      endDate: Time.formatOrNull(action.payload?.endDate),
      expirationDate: Time.formatOrNull(action.payload?.expirationDate),
      text: action.payload?.text,
      chargeTemplates: chargeTemplates,
      autoSendInvoiceToTenant: action?.payload?.autoSendInvoiceToTenant,
      isCommercial: action.payload?.isCommercial,
      // agencyFee: !!action.payload?.agencyFee ? {
      //   fixedFee: action.payload?.agencyFee.fixedFee,
      //   percentageFee: action.payload?.agencyFee.percentageFee,
      //   currency: action.payload?.agencyFee.currency.getCurrencyId(),
      //   fixedPlusPercentageMode: action.payload?.agencyFee.fixedPlusPercentageMode.id
      // } : null
    });
    // Automatically create charge templates.
    // yield res.data.contract.chargeTemplates.map(rawChargeTemplate => {
    //   const chargeTemplate: ChargeTemplateInterface = ChargeTemplateFactory.create(rawChargeTemplate);
    //   return put({
    //     type: Constants.GLOBAL_CHARGE_TEMPLATE_CREATE_SUCCESS,
    //     payload: {
    //       chargeTemplate: chargeTemplate
    //     }
    //   });
    // });

    const body = res.data.data;
    const contract: any = isContractIdOnly(body) ? body : ContractFactory.create(body);

    // yield put({
    //   type: Constants.GLOBAL_CONTRACT_CREATE_SUCCESS,
    //   payload: {
    //     contract: contract
    //   }
    // });

    // if (action.hasOwnProperty("onSuccess") === true) {
    //   action.onSuccess.call(this, contract);
    // }

    if (res.status === 207) {
      yield put({
        type: Constants.GLOBAL_REFUSAL_DIALOG_OPEN,
        payload: {
          titleKey: "partial-title-contract-renew",
          bodyKey: "partial-body-contract-renew"
        }
      });
    } else {
      yield put({
        type: Constants.SNACKBAR_ADD_MESSAGE,
        payload: {
          message: Translator.trans("snackbar-message-contract-renewed-successfully"),
          type: "success"
        }
      });
    }

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, contract);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action, EXPECTED_ERRORS);

    yield put({
      type: Constants.GLOBAL_CONTRACT_EXTEND_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

export function* watchContractExtend() {
  yield takeEvery(Constants.GLOBAL_CONTRACT_EXTEND_START, contractExtend);
}
